import { T } from '@/util/Localize';
import { formatWeatherDate } from '../cart-weather';
import type { DroneCeiling, DroneRestriction } from './drone-api';

/** The airspace for one date (`null`: no date picked yet), or a failed check. */
export interface DroneAirspaceAnswer {
    shootDate: string | null;
    ceiling?: DroneCeiling;
    failed?: boolean;
}

export interface DroneNotice {
    /** `warning` when a date is no-fly, `info` for restrictions to know about. */
    severity: 'warning' | 'info';
    heading: string;
    lines: string[];
    /** The picked dates the FAA rule refuses drone work on, `YYYY-MM-DD`. */
    noFlyDates: string[];
}

const SOURCE_LABELS: Record<string, string> = {
    nsufr: 'national security areas',
    'special-use-airspace': 'special use airspace',
    'dc-frz-sfra': 'the Washington DC FRZ/SFRA',
    tfr: 'temporary flight restrictions',
    stadiums: 'stadiums'
};

function label(restriction: DroneRestriction, noFly: boolean, undated: boolean): string {
    if (noFly) return T('No-fly');
    switch (restriction.effect) {
        case 'prohibited':
            return restriction.kind === 'tfr' && undated
                ? T('Temporary flight restriction')
                : T('Prohibited');
        case 'authorization-required':
            return T('Authorization required');
        default:
            return T('Advisory');
    }
}

function line(restriction: DroneRestriction, noFly: boolean, undated: boolean): string {
    let text = `${label(restriction, noFly, undated)}: ${restriction.name}`;
    if (restriction.detail) text += ` - ${restriction.detail}`;
    if (restriction.kind === 'tfr' && (restriction.effective || restriction.expires)) {
        text += ` (UTC ${restriction.effective ?? '?'} ${T('to')} ${restriction.expires ?? T('until further notice')})`;
    }
    return text;
}

/**
 * ROBO-854. What a page says about the airspace for its drone products: the API's `noFly` (the placement rule) decides the
 * warning, never this function. Every restriction, the controlled-airspace ceiling (ruling 6) and any source that could not be
 * read (ruling 7) is a line. `null` when there are no drone products, no answer yet, or nothing to say (an uncontrolled US
 * point with no restrictions, or a point outside the FAA's map).
 */
export function droneNotice(
    droneProducts: readonly string[],
    answers: readonly DroneAirspaceAnswer[]
): DroneNotice | null {
    if (droneProducts.length === 0 || answers.length === 0) return null;

    const lines: string[] = [];
    const seen = new Set<string>();
    const noFlyDates: string[] = [];
    let failed = false;
    const unchecked = new Set<string>();
    let controlled: DroneCeiling | undefined;

    for (const answer of answers) {
        if (answer.failed || !answer.ceiling) {
            failed = failed || !!answer.failed;
            continue;
        }
        const ceiling = answer.ceiling;
        if (!ceiling.covered) continue;
        if (ceiling.noFly && answer.shootDate) noFlyDates.push(answer.shootDate);
        if (ceiling.controlledAirspace) controlled = ceiling;
        for (const source of ceiling.unchecked ?? []) unchecked.add(source);
        for (const restriction of ceiling.restrictions ?? []) {
            const noFly = !!restriction.noFly;
            const key = `${restriction.kind}|${restriction.name}|${restriction.notamId ?? ''}|${noFly}`;
            if (seen.has(key)) continue;
            seen.add(key);
            lines.push(line(restriction, noFly, answer.shootDate === null));
        }
    }

    if (controlled) {
        const airport = controlled.airport;
        const where = airport
            ? [airport.faaId, airport.name ? `(${airport.name})` : null].filter(Boolean).join(' ')
            : T('an airport');
        lines.push(
            `${T('Controlled airspace near')} ${where}: ${T('LAANC or FAA authorization needed, up to')} ${controlled.ceilingFt ?? 0} ${T('ft above ground')}`
        );
    }
    if (unchecked.size > 0) {
        lines.push(
            `${T('Could not check')}: ${[...unchecked].map(s => T(SOURCE_LABELS[s] ?? s)).join(', ')}.`
        );
    }
    if (failed) lines.push(T('Could not check: the FAA airspace service did not answer.'));
    if (lines.length === 0) return null;

    const products = droneProducts.join(', ');
    return noFlyDates.length > 0
        ? {
              severity: 'warning',
              heading: `${products}: ${T('no-fly zone on')} ${noFlyDates.map(formatWeatherDate).join(', ')}. ${T('Drone work cannot be flown then; pick another date or contact us.')}`,
              lines,
              noFlyDates
          }
        : {
              severity: 'info',
              heading: `${T('Flight restrictions for')} ${products}`,
              lines,
              noFlyDates
          };
}
